import { Modal } from './Modal';
import { Check, X, AlertOctagon } from 'lucide-react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { CustomEmojiPlayer } from '@/components/ui/CustomEmojiPlayer';

interface StatusModalProps {
    isOpen: boolean;
    onClose: () => void;
    type: 'success' | 'error' | 'loading' | 'confirm';
    title: string;
    message?: string;
    actionLabel?: string;
    onAction?: () => void;
    secondaryLabel?: string;
    onSecondaryAction?: () => void;
    glass?: boolean;
    emojiId?: string;
    emojiSize?: number;
}

export function StatusModal({ isOpen, onClose, type, title, message, actionLabel, onAction, secondaryLabel, onSecondaryAction, glass, emojiId, emojiSize }: StatusModalProps) {
    const { impactOccurred } = useHapticFeedback();

    const showEmoji = !!emojiId && type === 'success';
    const effectiveEmojiId = String(emojiId || '').replace(/^#/, '').trim();

    const getIcon = () => {
        switch (type) {
            case 'success': return <Check strokeWidth={3} className="w-6 h-6 text-[#B4F73D] drop-shadow-[0_0_5px_rgba(180,247,61,0.5)]" />;
            case 'error': return <X strokeWidth={3} className="w-6 h-6 text-red-500 drop-shadow-[0_0_5px_rgba(239,68,68,0.5)]" />;
            case 'loading': return <div className="w-6 h-6 border-[3px] border-white/20 border-t-[#B4F73D] rounded-full animate-spin shadow-[0_0_10px_rgba(180,247,61,0.2)]" />;
            case 'confirm': return <AlertOctagon strokeWidth={2.5} className="w-6 h-6 text-orange-400 drop-shadow-[0_0_5px_rgba(251,146,60,0.5)]" />;
            default: return <AlertOctagon className="w-6 h-6 text-gray-400" />;
        }
    };

    const getGlow = () => {
        switch (type) {
            case 'success': return 'shadow-[0_0_25px_rgba(180,247,61,0.15)] border-[#B4F73D]/30 bg-[#B4F73D]/10';
            case 'error': return 'shadow-[0_0_25px_rgba(239,68,68,0.15)] border-red-500/30 bg-red-500/10';
            case 'confirm': return 'shadow-[0_0_25px_rgba(251,146,60,0.15)] border-orange-400/30 bg-orange-500/10';
            default: return 'border-white/10 bg-white/5';
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="" glass={glass}>
            <div className="flex flex-col items-center justify-center p-2 text-center">
                {showEmoji ? (
                    <div className="mb-4">
                        <CustomEmojiPlayer emojiId={effectiveEmojiId} size={emojiSize || 96} glowColor="#B4F73D" />
                    </div>
                ) : (
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border backdrop-blur-md mb-4 ${getGlow()}`}>
                        {getIcon()}
                    </div>
                )}

                <h3 className="text-xl font-black text-white tracking-widest uppercase mb-1 drop-shadow-md" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {title}
                </h3>
                {message && <p className="text-gray-400 text-[11px] font-bold tracking-wide italic leading-relaxed max-w-[85%]">{message}</p>}

                <div className="w-full space-y-2.5 mt-6">
                    {onAction && (
                        <button
                            onClick={() => {
                                impactOccurred('light');
                                onAction();
                                if (type !== 'loading') onClose();
                            }}
                            className={`w-full py-3.5 text-black font-black uppercase tracking-widest text-[11px] rounded-xl transition-all active:scale-[0.98] ${type === 'error' || type === 'confirm'
                                    ? 'bg-red-500 hover:bg-red-400 shadow-[0_0_15px_rgba(239,68,68,0.3)]'
                                    : 'bg-[#B4F73D] hover:bg-[#a1df35] shadow-[0_0_15px_rgba(180,247,61,0.3)]'
                                }`}
                        >
                            {actionLabel || 'Acknowledge'}
                        </button>
                    )}

                    {type === 'confirm' && (
                        <button
                            onClick={() => {
                                impactOccurred('light');
                                onSecondaryAction?.();
                                onClose();
                            }}
                            className="w-full py-3.5 bg-[#1C1C1C] hover:bg-[#2A2A2A] border border-white/10 text-white font-black uppercase tracking-widest text-[11px] rounded-xl transition-all active:scale-[0.98]"
                        >
                            {secondaryLabel || 'Deny'}
                        </button>
                    )}
                </div>
            </div>
        </Modal>
    );
}
